import api from '../Api';
import { BaseService } from '../BaseService';
import { FormDataCLT, SimulacaoParams } from '../../models/SimulationBank/SimulationBankModels';
import { coletarDispositivo } from '../../utils/deviceEvidence';

export class SimulationBankService extends BaseService {
    async getSimulationBlockStatus(): Promise<any[]> {
        try {
            return this.handleResponse(await api.get('simulation/screening/simulation-block'));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async setSimulationBlockStatus(isBlocked: boolean): Promise<any[]> {
        try {
            return this.handleResponse(await api.put('simulation/screening/simulation-block', {
                is_blocked: isBlocked ? 'True' : 'False',
            }));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    // Consulta cadastral por CPF usada no autopreenchimento do Step1. Roda em segundo
    // plano e nunca bloqueia o parceiro: qualquer falha volta pelo catchHandler e é
    // silenciosamente ignorada por quem chama (ver AI-1204).
    async getClienteByCpf(cpf: string): Promise<any[]> {
        try {
            const digits = cpf.replace(/\D/g, '');
            return this.handleResponse(await api.get(`simulation/screening/cpf/${digits}`));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    // Histórico de simulações do operador logado (SB-1953). Não recebe identificador de
    // parceiro/dependente de propósito: o backend resolve os dois a partir do usuário
    // logado e os envia por header, de modo que a tela não tem como pedir o histórico de
    // outro operador. Fora dos limites de limit/offset a API responde 422 sem chegar a
    // chamar o integrador.
    async listSimulationHistory(params: { limit?: number; offset?: number } = {}): Promise<any[]> {
        try {
            const { limit = 50, offset = 0 } = params;
            return this.handleResponse(await api.get('simulation/screening/history', { params: { limit, offset } }));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async getFinanceiras(): Promise<any[]> {
        try {
            return this.handleResponse(await api.get('simulation/screening/financials'));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async getLastSelectedFinancials(): Promise<any[]> {
        try {
            return this.handleResponse(await api.get('simulation/screening/financials/last-selected'));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async listDigitations(payload: Record<string, any>): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/digitations/list', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async getTriagem(formData: FormDataCLT): Promise<any[]> {
        try {
            const sexoMap: Record<string, string> = { M: 'MASCULINO', F: 'FEMININO', O: 'OUTRO' };
            const payload: Record<string, any> = {
                nome: formData.nome_completo,
                cpf: formData.cpf.replace(/\D/g, ''),
                data_nascimento: formData.data_nascimento,
                telefone: formData.telefone.replace(/\D/g, ''),
                estado: formData.estado,
                cidade: formData.cidade,
                data_admissao: formData.data_admissao || null,
                tempo_admissao: formData.tempo_admissao ?? null,
            };
            if (formData.alfabetizado) payload.alfabetizacao = formData.alfabetizado === 'true';
            if (formData.mobilidade_reduzida) payload.mobilidade_reduzida = formData.mobilidade_reduzida === 'true';
            if (formData.sexo) payload.sexo = sexoMap[formData.sexo] ?? formData.sexo;
            if (formData.nacionalidade) payload.nacionalidade = formData.nacionalidade.toUpperCase();
            if (formData.legado) payload.consignado_legado = formData.legado === 'true';
            if (formData.data_inicio_cnpj) payload.data_inicio_cnpj = formData.data_inicio_cnpj;
            if (formData.quantidade_funcionarios != null) payload.quantidade_funcionarios = formData.quantidade_funcionarios;
            if (formData.tipo_produto) payload.tipo_produto = formData.tipo_produto;
            if (formData.parcela_minima != null) payload.parcela_minima = formData.parcela_minima;
            // Financeiras escolhidas no modal de autorização (ver FinanceirasConsentModal) —
            // mesmo padrão de simulateScreening, que já envia uma lista de financeiras.
            if (formData.financeiras?.length) payload.financeiras = formData.financeiras;
            // AI-1277 — evidência do dispositivo para o aceite automático da Soma. Sem ela
            // a consulta segue normalmente (user agent e IP o back lê dos headers).
            const dispositivo = await coletarDispositivo();
            if (dispositivo) payload.dispositivo = dispositivo;
            return this.handleResponse(await api.post('simulation/screening/consult', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async registerLGPD(payload: { financeiras: (number | string)[]; form_data: FormDataCLT }): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/lgpd/authorize', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async simulateScreening(payload: {
        journey_id: string;
        modo: 'VALOR_PARCELA' | 'VALOR_DESEJADO';
        valor: number;
        qtde_parcelas: number;
        financeiras: string[];
    }): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/screening/simulate', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async pollScreening(screeningId?: string | number): Promise<any[]> {
        try {
            const payload = screeningId != null ? { journey_id: String(screeningId) } : {};
            return this.handleResponse(await api.post('simulation/screening/poll', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async getCommission(payload: { journey_id: string; password_report: string }): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/screening/commission', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async getStatus(simulationId: string | number): Promise<any[]> {
        try {
            return this.handleResponse(await api.get(`simulation/status/${simulationId}`));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async getDigitationForm(payload: { journey_id: string; financeira: string; cpf: string; uf: string }): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/digitations/form', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async validateDigitation(payload: {
        journey_id: string;
        step: 'VALIDAR' | 'EFETIVAR';
        payload: Record<string, any>;
        // Só faz efeito no step 'EFETIVAR' (ignorado em 'VALIDAR') — ver checkPartnerCommission.
        logged_typing_agent_id?: string;
    }): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/digitations/validation', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    // Checa se um parceiro (o logado, ou um terceiro informado como "agente de digitação")
    // está comissionado, antes de liberar a efetivação da proposta (ver StepConfirmacao).
    async checkPartnerCommission(payload: { partner_code: string }): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/digitations/partner-commission', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    async getFormalizationLink(journeyId: string | number, proposalCode: string): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/digitations/formalization-link', {
                journey_id: String(journeyId),
                codigo_proposta: proposalCode,
            }));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }

    // Peculiaridade do BMG: exige validação de um código de autorização enviado por SMS
    // ao cliente antes de liberar o resultado da consulta (ver Step3Cards/BMG).
    async validateBmgAuthorization(payload: { journey_id: string; codigo_autorizacao: number }): Promise<any[]> {
        try {
            return this.handleResponse(await api.post('simulation/screening/bmg-authorization', payload));
        } catch (e: any) {
            return this.catchHandler(e);
        }
    }
}